import React from 'react';
import { Droplet, Sun, Thermometer, UsersRound, Clock, Wind } from 'lucide-react';

// Define all possible parameter types
export type ParameterType =
    | 'pmv'
    | 'occupancy'
    | 'indoor-humidity'
    | 'indoor-temp'
    | 'outdoor-humidity'
    | 'outdoor-temp'
    | 'solar'
    | 'time_passed'
    | 'co2'
    | string; // Allow for dynamic parameters

// Define the parameter configuration interface
export interface ParameterConfig {
    icon: React.ReactNode;
    text: string;
    color: string;
    textColor: string;
}

// Create a mapping of parameter types to their configurations
export const parameterConfigs: Record<string, ParameterConfig> = {
    pmv: {
        icon: <UsersRound className="h-3 w-3" />,
        text: 'PMV',
        color: 'bg-[#A2E3A64D]',
        textColor: 'text-black-green'
    },
    occupancy: {
        icon: <UsersRound className="h-3 w-3" />,
        text: 'Occupancy',
        color: 'bg-bleached-green',
        textColor: 'text-black-green'
    },
    'indoor-humidity': {
        text: 'Indoor Humidity',
        icon: <Droplet className="h-3 w-3" />,
        color: 'bg-[#C0E2FF]',
        textColor: 'text-black-blue'
    },
    'indoor_humidity': {
        text: 'Indoor Humidity',
        icon: <Droplet className="h-3 w-3" />,
        color: 'bg-[#C0E2FF]',
        textColor: 'text-black-blue'
    },
    'indoor-temp': {
        text: 'Indoor Temp',
        icon: <Thermometer className="h-3 w-3" />,
        color: 'bg-[#C0E2FF]',
        textColor: 'text-black-blue'
    },
    'indoor_temp': {
        text: 'Indoor Temp',
        icon: <Thermometer className="h-3 w-3" />,
        color: 'bg-[#C0E2FF]',
        textColor: 'text-black-blue'
    },
    'outdoor-humidity': {
        text: 'Outdoor Humidity',
        icon: <Droplet className="h-3 w-3" />,
        color: 'bg-[#EA663F4D]',
        textColor: 'text-black-orange'
    },
    'outdoor_humidity': {
        text: 'Outdoor Humidity',
        icon: <Droplet className="h-3 w-3" />,
        color: 'bg-[#EA663F4D]',
        textColor: 'text-black-orange'
    },
    'outdoor-temp': {
        text: 'Outdoor Temp',
        icon: <Thermometer className="h-3 w-3" />,
        color: 'bg-[#EA663F4D]',
        textColor: 'text-black-orange'
    },
    'outdoor_temp': {
        text: 'Outdoor Temp',
        icon: <Thermometer className="h-3 w-3" />,
        color: 'bg-[#EA663F4D]',
        textColor: 'text-black-orange'
    },
    solar: {
        text: 'Solar Surplus Rate',
        icon: <Sun className="h-3 w-3" />,
        color: 'bg-bleached-orange',
        textColor: 'text-black-orange'
    },
    'solar_surplus_rate': {
        text: 'Solar Surplus Rate',
        icon: <Sun className="h-3 w-3" />,
        color: 'bg-bleached-orange',
        textColor: 'text-black-orange'
    },
    'solar_surplus': {
        text: 'Solar Surplus',
        icon: <Sun className="h-3 w-3" />,
        color: 'bg-bleached-orange',
        textColor: 'text-black-orange'
    },
    'time_passed': {
        text: 'Time Passed',
        icon: <Clock className="h-3 w-3" />,
        color: 'bg-[#EDEFF9]',
        textColor: 'text-dark-black'
    },
    'co2': {
        text: 'CO2',
        icon: <Wind className="h-3 w-3" />,
        color: 'bg-[#D1F4D9]',
        textColor: 'text-black-green'
    }
};

// Helper function to get parameter configuration
export const getParameterConfig = (parameter: string): ParameterConfig => {
    // Try to get the config for the parameter
    const config = parameterConfigs[parameter];

    // If the config exists, return it
    if (config) {
        return config;
    }

    // Otherwise, return a default config
    return {
        icon: null,
        text: parameter,
        color: 'bg-[#CBF0EF]',
        textColor: 'text-black'
    };
}; 